import rawComponents from "../../../resources/generated/components.json";
import commonAdapterNameService from "./common-adapter-name-service";

/**
 * Type representing all valid component IDs from the components.json file.
 */
export type componentID = keyof typeof rawComponents;

/**
 * Components mapped as a typed record for safer access.
 */
const Components = (rawComponents as unknown) as Record<componentID, Component>;

/**
 * Represents any key-value structure for prefill properties.
 */
interface PrefillProperties {
    [key: string]: any;
}

type adapterType =
    | "SOURCE"
    | "TARGET"
    | "CQ"
    | "CACHE"
    | "STREAM"
    | "ROUTER"
    | "WINDOW"
    | "EXTERNALSOURCE"
    | "EXTERNALCACHE"
    | "SENTINEL"
    | "WACTIONSTORE"
    | "OPENPROCESSOR";

export type modeValues = "IL" | "CDC";
export type modes = modeValues[];

/**
 * Enum representing all the categories available in a component object.
 */
export enum AdapterCategory {
    CLOUD_STORAGE = "CLOUD STORAGE",
    EVENT_BUSES = "EVENT BUSES",
    DATABASES = "DATABASES",
    DATA_WAREHOUSES = "DATA WAREHOUSES",
    APPLICATIONS = "APPLICATIONS",
    MISCELLANEOUS = "MISCELLANEOUS",
    DB_EVENT_TRANSFORMERS = "DB EVENT TRANSFORMERS",
    EVENT_TRANSFORMERS = "EVENT TRANSFORMERS",
    BASE = "BASE",
    DATA_LAKES = "DATA LAKES"
}

/**
 * Enum representing all the keys available in a component object.
 */
export enum ComponentKeys {
    ComponentName = "Component Name",
    Type = "Type",
    HumanReadableName = "Human Readable Name",
    ImageID = "Image ID",
    NewlyReleased = "Newly Released",
    AdapterProperties = "Adapter Properties",
    Parser = "Parser",
    Adapter = "Adapter",
    ParserProperties = "Parser Properties",
    SpriteID = "Sprite ID",
    Category = "Category",
    AdditionalProperties = "Additional Properties",
    Mode = "Mode",
    // For clubbed adapters only
    ILModeInfo = "IL Mode Info",
    CDCModeInfo = "CDC Mode Info"
}

/**
 * Per mode metadata for a clubbed adapter's underlying IL or CDC component.
 * `isEnabled` is populated later (in getSources) from the adapter's license state.
 */
export interface ModeInfo {
    componentName: string;
    adapter?: string;
    isEnabled?: boolean;
}

/**
 * Enum representing all the keys available in a Adapter Properties object.
 */
export enum AdapterProperties {
    DatabaseProviderType = "DatabaseProviderType"
}
/**
 * Structure representing a single component with all its supported properties.
 */
export interface Component {
    [ComponentKeys.ComponentName]: string;
    [ComponentKeys.Type]: string;
    [ComponentKeys.HumanReadableName]: string;
    [ComponentKeys.ImageID]: string;
    [ComponentKeys.NewlyReleased]: "Yes" | "No";
    [ComponentKeys.AdapterProperties]?: object;
    [ComponentKeys.Parser]?: string;
    [ComponentKeys.Adapter]?: string;
    [ComponentKeys.ParserProperties]?: object;
    [ComponentKeys.SpriteID]?: string;
    [ComponentKeys.Category]?: string;
    [ComponentKeys.AdditionalProperties]?: object;
    [ComponentKeys.Mode]?: modes;
    [ComponentKeys.ILModeInfo]?: ModeInfo;
    [ComponentKeys.CDCModeInfo]?: ModeInfo;
}

/**
 * Generic interface for icon service.
 */
export interface IconService<ID, Data> {
    getIDs(): Array<ID>;
    getHumanReadableName(id: ID): string;
    getSpriteID(id: ID): string;
    getMetadata(id: ID): Data;
}

/**
 * Extended interface specifically for component ervice.
 */
interface ComponentServiceType extends IconService<componentID, Component> {
    getPrefillProperties(componentID: componentID): PrefillProperties;
    getAllComponents(): Component[];
    getType(componentID: componentID): adapterType;
    getIsNewlyReleased(componentID: componentID): boolean;
    getCategory(componentID: componentID): string;
    getAdapter(componentID: componentID): string;
    getAdapterProperties(componentID: componentID): object;
    getParser(componentID: componentID): string;
    getParserProperties(componentID: componentID): object;
    getAdditionalProperties(componentID: componentID): object;
    getCreateAppPageData(componentType?: "SOURCE" | "TARGET"): Component[];
    getComponentIDFromName(componentName: string, type: adapterType): componentID;
    getIconClassName(componentID: componentID): string;
    getComponentIDFromAdapterName(adapterName: string): componentID;
    getComponentIDFromParserName(parserName: string): componentID;
}

/**
 * Default sprite ID(striim icon) to use when none is specified in the sheets.
 */
const defaultStriimSpriteID = "F5";

/**
 * Service providing access to metadata and utilities for all  components.
 */
export class ComponentServiceBase implements ComponentServiceType {
    /**
     * Returns an array of all registered component IDs.
     */
    getIDs() {
        return Object.keys(Components) as Array<componentID>;
    }
    /**
     *
     * @returns all components
     */
    getAllComponents() {
        return Object.values(Components) as Component[];
    }

    /**
     * Gets the list of adapters along with their metadata to be shown on the Create App page.
     *
     * @param componentType
     * (Optional) Can be either "SOURCE" or "TARGET"
     * If componentType not provided, adapters of both "SOURCE" and "TARGET" types are returned.
     *
     * @returns `Component[]` A list of adapter and its metadata matching the specified type(s).
     */
    getCreateAppPageData(componentType?: "SOURCE" | "TARGET" | null) {
        if (!componentType) {
            const validCategories = ["SOURCE", "TARGET"];
            return Object.values(Components).filter(component =>
                validCategories.includes(component[ComponentKeys.Type])
            ) as Component[];
        }

        const filterComponents = Object.fromEntries(
            Object.entries(Components).filter(
                ([, component]) =>
                    component[ComponentKeys.Type] === componentType &&
                    (component[ComponentKeys.Adapter] || component[ComponentKeys.ComponentName] === "Striim")
            )
        ) as Record<componentID, Component>;

        componentType === "SOURCE" && commonAdapterNameService.clubAdapters(filterComponents);
        return Object.values(filterComponents) as Component[];
    }

    /**
     * @param componentID
     * Returns the human readable name of a component.
     * Falls back to the component ID if human readable name is not found.
     */
    getHumanReadableName(componentID: componentID | string) {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.HumanReadableName] ?? componentID;
    }

    /**
     * @param componentID
     * Returns the sprite ID for a component.
     * Returns striim sprite ID if not defined.
     */
    getSpriteID(componentID: componentID) {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.SpriteID] ?? defaultStriimSpriteID;
    }

    /**
     * @param componentID
     * Returns the full metadata object for a given component ID.
     */
    getMetadata(componentID: componentID): Component {
        this.checkIDPresent(componentID);
        return Components[componentID] as Component;
    }

    /**
     * @param componentID
     * Returns the icon class name (used for rendering sprite-based icons).
     * Defaults to `striim` if not defined.
     */
    getIconClassName(componentID: componentID) {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.ImageID] ?? "striim";
    }

    /**
     * @param componentID
     * Returns the adapter type of the component (SOURCE or TARGET).
     */
    getType(componentID: componentID): adapterType {
        this.checkIDPresent(componentID);
        return Components[componentID]?.Type as adapterType;
    }

    /**
     * @param componentID
     * Checks if the component is marked as newly released in the sheets.
     * @returns `boolean`
     */
    getIsNewlyReleased(componentID: componentID): boolean {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.NewlyReleased] === "Yes";
    }

    /**
     * @param componentID
     * Returns the category of the component.
     * @returns `category` | `null`
     */
    getCategory(componentID: componentID): string {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.Category] ?? null;
    }

    /**
     * @param componentID
     * Returns the adapter handler for the component.
     * Handler will be present for all sources and targets
     * The handler name will be the adapter name in the backend.
     */
    getAdapter(componentID: componentID): string {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.Adapter];
    }

    /**
     * @param componentID
     * Returns the adapter properties for the component.
     * Defaults to an empty object.
     */
    getAdapterProperties(componentID: componentID): Record<string, unknown> | {} {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.AdapterProperties] || {};
    }

    /**
     * @param componentID
     * Returns the parser handler for the component, if defined.
     * `parser` for sources and `formatter` for targets
     */
    getParser(componentID: componentID): string {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.Parser];
    }

    /**
     * @param componentID
     * Returns the `parser` or `formatter` configuration properties.
     * Defaults to an empty object.
     */
    getParserProperties(componentID: componentID): object {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.ParserProperties] || {};
    }

    /**
     * Returns any additional properties defined for the component.
     */
    getAdditionalProperties(componentID: componentID): object {
        this.checkIDPresent(componentID);
        return Components[componentID]?.[ComponentKeys.AdditionalProperties] || {};
    }

    /**
     * Validates the presence of a component ID.
     * Throws an error if the ID is missing or invalid.
     */
    protected checkIDPresent(componentID: string) {
        if (componentID === undefined || componentID === null) {
            throw new Error(`Invalid component ID: ${componentID}`);
        }
    }

    /**
     * Finds a component ID based on the given human readable name and adapter type.
     */
    getComponentIDFromName(componentName: string, adapterType = undefined) {
        return Object.values(Components).find(component => {
            if (adapterType) {
                return (
                    adapterType === component[ComponentKeys.Type] &&
                    component[ComponentKeys.HumanReadableName] === componentName
                );
            }
            return component[ComponentKeys.HumanReadableName].toLowerCase() === componentName.toLowerCase();
        })?.[ComponentKeys.ComponentName] as componentID;
    }
    getComponentIDFromAdapterName(adapterName: string) {
        return Object.values(Components).find(component => {
            return component[ComponentKeys.Adapter] === adapterName;
        })?.[ComponentKeys.ComponentName] as componentID;
    }

    getComponentIDFromParserName(parserName: string) {
        return Object.values(Components).find(component => {
            return component[ComponentKeys.Parser] === parserName;
        })?.[ComponentKeys.ComponentName] as componentID;
    }

    /**
     * Returns the icon image ID for a given adapter name.
     * For "DatabaseReader", uses the databaseProviderType to resolve the icon via a provider map.
     * Falls back to "striim" if no match is found.
     */
    getIconNameByAdapterNameDBType(adapterName: string, databaseProviderType?: string): string {
        if (adapterName === "DatabaseReader" && databaseProviderType) {
            const DB_PROVIDER_ICON_MAP: Record<string, string> = {
                Oracle: "oracle",
                Postgres: "postgresqlcdc",
                MySQL: "mysql",
                SQLServer: "mssql"
            };
            return DB_PROVIDER_ICON_MAP[databaseProviderType] ?? "striim";
        }
        const componentID = this.getComponentIDFromAdapterName(adapterName);
        return componentID ? this.getIconClassName(componentID) : "striim";
    }

    /**
     * Generates prefill configuration data for the component based on its type.
     * Includes adapter and parser/formatter handlers and their properties.
     */
    getPrefillProperties(componentID: componentID): PrefillProperties {
        this.checkIDPresent(componentID);
        const type = this.getType(componentID);
        const base = {
            adapter: {
                handler: this.getAdapter(componentID),
                properties: this.getAdapterProperties(componentID)
            }
        };

        switch (type) {
            case "SOURCE":
                const sourceParserData = this.getParser(componentID);
                return {
                    ...base,
                    ...(sourceParserData && {
                        parser: { handler: sourceParserData }
                    })
                };
            case "TARGET":
                const targetParserData = this.getParser(componentID);
                return {
                    ...base,
                    ...(targetParserData && {
                        formatter: { handler: targetParserData }
                    })
                };
            default:
                return this.getAdditionalProperties(componentID);
        }
    }

    /**
     * Checks if the given component name is valid.
     */
    checkIfValidComponent(componentName: string) {
        return Components.hasOwnProperty(componentName);
    }
}

/**
 * Singleton instance of the ComponentService.
 */
const componentServiceBase = new ComponentServiceBase();
export default componentServiceBase;
